import { ReactElement } from 'react'
import {
  SurveyFreeTextQuestion,
  SurveyFreeTextSection,
  SurveyLikertQuestion,
  SurveyLikertSection,
  surveyFreeTextQuestions,
  surveyLikertQuestions,
} from 'src/v3/components/survey'
import {
  CourseV2SurveyFreeTextKey,
  CourseV2SurveyLikertKey,
  CourseV2SurveySubmission,
} from 'src/v3/classroom-v2/types'

// The wizard's survey fields, composed from the shared survey in
// src/v3/components/survey.tsx — the same question tables, the same wording, the
// same field markup the Event and v1 classroom surveys render. Nothing about the
// questions is redefined here; this file only supplies where the answers come
// from and where changes go.
//
// It differs from that file's default export in one way, forced by this surface
// rather than chosen: no state. Values come from the wizard slice's draft and
// every change is dispatched back, so there is one copy of the answers and a
// remount can't leave the form and the store disagreeing. Same reasoning as
// QuizTaker.
//
// All thirteen questions are required here exactly as they are on the other two
// surfaces. The v2 API happens to accept nulls — its own validation is only
// 1–5-when-present — but that is a looser backend contract, not a different
// product rule, so the requirement is enforced client-side (see
// unansweredSurveyFields, and the surveySubmitAttempted listener that gates the
// POST on it).
export let SurveyForm = ({
  values,
  isDisabled,
  unansweredFields,
  onChangeLikert,
  onChangeFreeText,
}: {
  values: CourseV2SurveySubmission
  isDisabled: boolean
  // Which questions to mark as missing. Passed in rather than derived here so
  // the caller decides WHEN to show them — they stay hidden until the learner has
  // pressed Submit, so a fresh form isn't covered in errors.
  unansweredFields: (CourseV2SurveyLikertKey | CourseV2SurveyFreeTextKey)[]
  onChangeLikert: (name: CourseV2SurveyLikertKey, value: number) => void
  onChangeFreeText: (name: CourseV2SurveyFreeTextKey, value: string) => void
}): ReactElement => {
  let errorFor = (name: CourseV2SurveyLikertKey | CourseV2SurveyFreeTextKey) =>
    unansweredFields.includes(name) ? 'This field is required' : undefined

  return (
    <>
      <SurveyLikertSection>
        {surveyLikertQuestions.map((question, index) => (
          <SurveyLikertQuestion
            key={question.name}
            name={question.name}
            label={question.label}
            number={index + 1}
            value={values[question.name]}
            error={errorFor(question.name)}
            isRequired
            isDisabled={isDisabled}
            onChange={(value) => onChangeLikert(question.name, value)}
          />
        ))}
      </SurveyLikertSection>

      <SurveyFreeTextSection>
        {surveyFreeTextQuestions.map((question, index) => (
          <SurveyFreeTextQuestion
            key={question.name}
            name={question.name}
            label={question.label}
            number={index + 1}
            value={values[question.name]}
            error={errorFor(question.name)}
            isRequired
            isDisabled={isDisabled}
            onChange={(value) => onChangeFreeText(question.name, value)}
          />
        ))}
      </SurveyFreeTextSection>
    </>
  )
}
